import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "../lib/api";

/**
 * Vehicle Planning · fleet master + default driver assignment.
 * The default driver flows into Field Operations — picking a vehicle
 * on a mech pickup or SPB pre-fills its driver. Trip-level routing
 * (ramp → mill legs, capacity balancing) is a later slice on the
 * existing vehicle_trip schema.
 */

interface Worker {
  id: string;
  name: string;
  workerType: string;
}
interface Vehicle {
  id: string;
  plateNumber: string;
  vehicleType: string;
  capacityKg: string | null;
  capacityBunches: number | null;
  defaultDriverWorkerId: string | null;
  isActive: boolean;
}

const VEHICLE_TYPES = [
  "TRACTOR_TRAILER",
  "MINI_LORRY",
  "LORRY_3T",
  "LORRY_5T",
  "LORRY_10T",
  "GRABBER",
] as const;

export function VehiclePlanning() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const vehiclesQ = useQuery({
    queryKey: ["vehicles"],
    queryFn: () => api.list<Vehicle>("/v1/vehicles"),
  });
  const workersQ = useQuery({
    queryKey: ["workers"],
    queryFn: () => api.list<Worker>("/v1/workers?limit=500"),
  });
  const drivers = (workersQ.data?.data ?? []).filter(
    (w) => w.workerType === "DRIVER",
  );

  const [plate, setPlate] = useState("");
  const [vType, setVType] = useState<string>("LORRY_5T");
  const [capBunches, setCapBunches] = useState("");
  const [newDriverId, setNewDriverId] = useState("");

  const run = (fn: () => Promise<unknown>) => async () => {
    setError(null);
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ["vehicles"] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const addVehicle = run(async () => {
    await api.post("/v1/vehicles", {
      plateNumber: plate,
      vehicleType: vType,
      capacityBunches: capBunches ? Number(capBunches) : undefined,
      defaultDriverWorkerId: newDriverId || undefined,
    });
    setPlate("");
    setCapBunches("");
    setNewDriverId("");
  });

  const sel = "rounded border border-border px-2 py-1 text-xs bg-surface-0";

  return (
    <div className="space-y-6 text-sm">
      <div>
        <h1 className="text-lg font-medium">Vehicle Planning</h1>
        <p className="text-xs text-text-secondary">
          Fleet master. The default driver flows into Field Operations —
          picking a vehicle on a pickup or SPB pre-fills its driver.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
          {error}
        </div>
      )}

      <section className="rounded border border-border">
        <div className="flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2">
          <div className="font-medium">
            Fleet ({vehiclesQ.data?.data.length ?? 0})
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3 text-xs">
          <label className="flex flex-col gap-1">
            <span>Plate number</span>
            <input
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
              placeholder="SAB 5678 M"
              className={sel}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span>Type</span>
            <select
              value={vType}
              onChange={(e) => setVType(e.target.value)}
              className={sel}
            >
              {VEHICLE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span>Capacity (bunches)</span>
            <input
              type="number"
              min={0}
              value={capBunches}
              onChange={(e) => setCapBunches(e.target.value)}
              className={sel + " w-28"}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span>Default driver</span>
            <select
              value={newDriverId}
              onChange={(e) => setNewDriverId(e.target.value)}
              className={sel}
            >
              <option value="">— none —</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={addVehicle}
            disabled={!plate}
            className="rounded bg-text-primary px-3 py-1.5 text-xs text-white hover:opacity-90 disabled:opacity-40"
          >
            Add vehicle
          </button>
        </div>

        <table className="w-full text-xs">
          <thead className="border-b border-border bg-surface-1 text-text-secondary">
            <tr>
              <th className="p-2 text-left">Plate</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-right">Capacity (bunches)</th>
              <th className="p-2 text-left">Default driver</th>
              <th className="p-2 text-center">Active</th>
            </tr>
          </thead>
          <tbody>
            {(vehiclesQ.data?.data ?? []).map((v) => (
              <tr key={v.id} className="border-b border-border">
                <td className="p-2 font-mono font-medium">{v.plateNumber}</td>
                <td className="p-2">{v.vehicleType.replace(/_/g, " ")}</td>
                <td className="p-2 text-right tabular-nums">
                  {v.capacityBunches ?? "—"}
                </td>
                <td className="p-2">
                  <select
                    value={v.defaultDriverWorkerId ?? ""}
                    onChange={(e) =>
                      void run(() =>
                        api.patch(`/v1/vehicles/${v.id}`, {
                          defaultDriverWorkerId: e.target.value || undefined,
                        }),
                      )()
                    }
                    className={sel}
                  >
                    <option value="">— none —</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-2 text-center">
                  <input
                    type="checkbox"
                    checked={v.isActive}
                    onChange={(e) =>
                      void run(() =>
                        api.patch(`/v1/vehicles/${v.id}`, {
                          isActive: e.target.checked,
                        }),
                      )()
                    }
                  />
                </td>
              </tr>
            ))}
            {!vehiclesQ.data?.data.length && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-text-secondary">
                  No vehicles yet. Add one above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
